import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { weeklyOperatingHoursApi } from '@/lib/api/weekly-operating-hours.api';
import { queryKeys } from '@/lib/query-client';
import type {
  CreateWeeklyOperatingHoursRequest,
  UpdateWeeklyOperatingHoursRequest
} from '@geely/shared/types';

interface UseWeeklyOperatingHoursOptions {
  enabled?: boolean;
}

export function useWeeklyOperatingHours(storeId: string | undefined, options: UseWeeklyOperatingHoursOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.weeklyOperatingHours(storeId || ''),
    queryFn: async () => {
      const response = await weeklyOperatingHoursApi.list({ storeId: storeId! });
      return response.data;
    },
    enabled: enabled && !!storeId,
  });
}

export function useCreateWeeklyOperatingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateWeeklyOperatingHoursRequest) => {
      const response = await weeklyOperatingHoursApi.create(data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.weeklyOperatingHours(variables.storeId),
      });
    },
  });
}

export function useUpdateWeeklyOperatingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
      storeId,
    }: {
      id: string;
      data: UpdateWeeklyOperatingHoursRequest;
      storeId: string;
    }) => {
      const response = await weeklyOperatingHoursApi.update(id, data);
      return { data: response.data, storeId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.weeklyOperatingHours(variables.storeId),
      });
    },
  });
}

export function useDeleteWeeklyOperatingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, storeId }: { id: string; storeId: string }) => {
      await weeklyOperatingHoursApi.delete(id);
      return { storeId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.weeklyOperatingHours(result.storeId),
      });
    },
  });
}
